import { useState, useEffect, useCallback, useRef } from "react";

/**
 * useWebSocket hook
 * Connects to backend WebSocket for real-time session updates
 */
export function useWebSocket({ onSessionEvent, onSyncEvent, enabled = true }) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const connect = useCallback(() => {
    if (!enabled) return;

    // Build WebSocket URL
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.hostname;
    const isStaging = window.location.pathname.startsWith('/staging');
    const wsPath = isStaging ? '/staging/ws' : '/ws';

    // In local dev (port 5173), connect to backend on port 3001
    // In production, nginx proxies /ws to backend
    const isLocalDev = window.location.port === '5173';
    const wsHost = isLocalDev ? `${host}:3001` : host;
    const wsUrl = `${protocol}//${wsHost}${wsPath}`;

    console.log("🔌 WebSocket connecting to:", wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("🔌 WebSocket connected");
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setLastMessage(message);

          // Handle different message types
          if (message.type === "session" && onSessionEvent) {
            onSessionEvent(message.data);
          } else if (message.type === "sync" && onSyncEvent) {
            onSyncEvent(message.data);
          }
        } catch (err) {
          console.error("WebSocket message parse error:", err);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = (event) => {
        console.log("🔌 WebSocket disconnected, code:", event.code);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (enabled && reconnectAttempts.current < 10) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };
    } catch (err) {
      console.error("WebSocket connection error:", err);
    }
  }, [enabled, onSessionEvent, onSyncEvent]);

  // Connect on mount
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [enabled, connect]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  return {
    isConnected,
    lastMessage,
    reconnect,
  };
}

export default useWebSocket;
